import React from "react"
import { FormFeedback, FormGroup, FormText, Input, Label } from "reactstrap"
import DateTimeField from "components/Common/DateTimeField"
import { logisticsMessage } from "./useLogisticsOperation"
import { courierNames } from "./shipmentLabels"
import { PARCELS_LIMIT } from "./ParcelSelector"

// The rules the API checks a shipment's transport with (common/logistics): an AWB as printed, whole parcels, an https tracking link. The
// sheet's validation checks its transport with them too (SheetValidationDialogs.js).
export const AWB = /^[A-Za-z0-9-]{4,40}$/
export const httpsLink = value => {
  try { return new URL(value).protocol === "https:" } catch { return false }
}

// A shipment's transport as its form holds it, from how it left or, for a new one, a Cargus AWB on today. Cargus's tracking link comes
// from Settings, so only DPD's or another courier's is kept.
export const transportForm = (shipment = {}, today = "") => ({ transport: shipment.transport || "courier", courier: shipment.courier || "cargus",
  courierName: shipment.courierName || "", awb: shipment.awb || "", parcels: shipment.parcels ? String(shipment.parcels) : "1",
  trackingUrl: shipment.courier && shipment.courier !== "cargus" ? shipment.trackingUrl || "" : "", note: shipment.note || "", date: shipment.date || today })

// What is wrong with a transport's fields, by field, and whether it says all its transport needs: a courier's AWB (and another
// courier's name), or the note of a shipment without one.
export const checkTransport = (form, today) => {
  const courier = form.transport === "courier"
  const awb = form.awb.trim()
  const linkable = courier && form.courier !== "cargus"
  const problems = {
    awb: courier && awb && !AWB.test(awb) ? logisticsMessage("INVALID_LOGISTICS_AWB") : null,
    parcels: courier && !(/^\d+$/.test(form.parcels) && Number(form.parcels) >= 1 && Number(form.parcels) <= PARCELS_LIMIT)
      ? `Enter a whole number of parcels, from 1 to ${PARCELS_LIMIT}.` : null,
    trackingUrl: linkable && form.trackingUrl.trim() && !httpsLink(form.trackingUrl.trim()) ? "Paste an https link, or leave it empty." : null,
    date: today && (!form.date || form.date > today) ? "Choose today or an earlier day." : null,
  }
  const complete = courier ? AWB.test(awb) && (form.courier !== "other" || Boolean(form.courierName.trim())) : Boolean(form.note.trim())
  return { problems, valid: complete && Object.values(problems).every(problem => !problem) }
}

// How a shipment left, as a command states it: the courier's AWB with its parcels (another courier's name, DPD's or another courier's
// link), or the note; and the day.
export const transportBody = form => {
  const linkable = form.courier !== "cargus"
  const how = form.transport === "courier"
    ? { courier: form.courier, awb: form.awb.trim(), parcels: Number(form.parcels), ...(form.courier === "other" ? { courierName: form.courierName.trim() } : {}),
      ...(linkable && form.trackingUrl.trim() ? { trackingUrl: form.trackingUrl.trim() } : {}) }
    : { note: form.note.trim() }
  return { transport: form.transport, ...how, date: form.date }
}

// The fields of a transport: a courier's AWB entered by hand, with its parcels (Cargus, DPD or another courier, named; DPD's or another
// courier's tracking link may be pasted), or the note the club and the organizer see; then the day it left, today or earlier.
export default function TransportFields({ form, change, problems, today }) {
  const linkable = form.courier !== "cargus"
  return <>
    {form.transport === "courier" ? <div className="form-row">
      <FormGroup className="col-sm-4">
        <Label for="logistics-courier">Courier</Label>
        <Input id="logistics-courier" type="select" value={form.courier} onChange={change("courier")}>
          {Object.entries(courierNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Input>
      </FormGroup>
      {form.courier === "other" && <FormGroup className="col-sm-8">
        <Label for="logistics-courier-name">Courier name</Label>
        <Input id="logistics-courier-name" maxLength={60} value={form.courierName} onChange={change("courierName")} placeholder="e.g. Speedy" />
      </FormGroup>}
      <FormGroup className="col-sm-8">
        <Label for="logistics-awb">AWB</Label>
        <Input id="logistics-awb" value={form.awb} onChange={change("awb")} invalid={Boolean(problems.awb)} placeholder="e.g. 1234567890" />
        {problems.awb ? <FormFeedback>{problems.awb}</FormFeedback> : <FormText>Created in the courier's application or with the AI prompt.</FormText>}
      </FormGroup>
      <FormGroup className="col-sm-4">
        <Label for="logistics-parcels">Parcels</Label>
        <Input id="logistics-parcels" type="number" min={1} max={PARCELS_LIMIT} step={1} value={form.parcels} onChange={change("parcels")}
          invalid={Boolean(problems.parcels)} />
        {problems.parcels && <FormFeedback>{problems.parcels}</FormFeedback>}
      </FormGroup>
      {linkable && <FormGroup className="col-12">
        <Label for="logistics-tracking">Tracking link (optional)</Label>
        <Input id="logistics-tracking" maxLength={500} value={form.trackingUrl} onChange={change("trackingUrl")} invalid={Boolean(problems.trackingUrl)} />
        {problems.trackingUrl ? <FormFeedback>{problems.trackingUrl}</FormFeedback> : <FormText>Shown to the club and the organizer.</FormText>}
      </FormGroup>}
    </div> : <FormGroup>
      <Label for="logistics-note">Note</Label>
      <Input id="logistics-note" type="textarea" rows={2} maxLength={500} value={form.note} onChange={change("note")}
        placeholder="e.g. picked up by the organizer at Câmpina" />
      <FormText>The club and the organizer will see this note.</FormText>
    </FormGroup>}
    <FormGroup>
      <Label for="logistics-shipping-date">Shipping date</Label>
      <DateTimeField id="logistics-shipping-date" mode="date" max={today} value={form.date} onChange={change("date")} invalid={Boolean(problems.date)} />
      {problems.date && <FormFeedback>{problems.date}</FormFeedback>}
    </FormGroup>
  </>
}
